import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles, ChevronRight, Minus, Maximize2, SkipForward, List, Check, Play, Camera } from "lucide-react";
import { useTour } from "../lib/tour";
import { embedForUrl } from "../lib/videoEmbed";

// The spotlight tour overlay: dims the whole page, cuts out a highlight
// around the target element identified by `data-testid`, and floats a
// tooltip card next to it with "Skip" and primary buttons.
export default function TourOverlay() {
  const tour = useTour();
  const [rect, setRect] = useState(null);

  const step = tour?.step;

  // Measure the target element and re-measure on scroll/resize + at a light
  // interval so as the target page mounts asynchronously (routes lazy-load),
  // the spotlight snaps into place instead of missing on first paint.
  useLayoutEffect(() => {
    if (!step || step.centered || !step.testid) {
      setRect(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${step.testid}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        bottom: r.bottom,
      });
      // Scroll target into view if it's off-screen
      if (r.top < 40 || r.bottom > window.innerHeight - 200) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    measure();
    const int = setInterval(measure, 400);
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => {
      clearInterval(int);
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [step]);

  if (!tour?.running || !step) return null;

  const isCentered = step.centered || !rect;
  const isDockedBottom = step.dock === "bottom" && !isCentered;

  // Position the tooltip. Priority:
  //   1. Docked steps (form-heavy pages like add-txn/add-account) → pinned to
  //      the bottom of the viewport, above the bottom nav, so the form stays
  //      reachable no matter how the target renders.
  //   2. Otherwise: below the target if there's room, else above it.
  let tooltipStyle = {};
  if (isDockedBottom) {
    tooltipStyle = {
      position: "fixed",
      left: "50%",
      // Dock at the TOP of the viewport so the entire form (including the
      // Save button at the bottom) stays reachable via normal scroll. Sits
      // just below the SyncBook page chrome.
      top: 12,
      transform: "translateX(-50%)",
      width: "min(360px, calc(100vw - 24px))",
      zIndex: 10001,
    };
  } else if (!isCentered && rect) {
    const spaceBelow = window.innerHeight - rect.bottom;
    const showBelow = spaceBelow > 260;
    tooltipStyle = {
      position: "fixed",
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 344)),
      top: showBelow ? rect.bottom + 16 : Math.max(12, rect.top - 260),
      width: "min(340px, calc(100vw - 24px))",
      zIndex: 10001,
    };
  }

  return createPortal(
    <div data-testid="tour-overlay" className="fixed inset-0 z-[9999] pointer-events-none">
      {/* Dim layer — uses SVG mask when we have a target rect so the spotlight
          hole is precise. `pointer-events: none` lets clicks pass through to
          the highlighted UI. Fallback centered steps get a plain dim div.
          Hidden entirely when the user minimises the tour card so they can
          freely fill in whichever input the card was blocking. */}
      {tour.collapsed && !isCentered ? null : isCentered ? (
        <div className="absolute inset-0 bg-black/60 pointer-events-auto" data-testid="tour-dim" />
      ) : (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          data-testid="tour-dim"
        >
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={Math.max(0, rect.left - 8)}
                y={Math.max(0, rect.top - 8)}
                width={rect.width + 16}
                height={rect.height + 16}
                rx="14"
                ry="14"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.6)"
            mask="url(#tour-mask)"
          />
          {/* Spotlight border ring around the target */}
          <rect
            x={Math.max(0, rect.left - 8)}
            y={Math.max(0, rect.top - 8)}
            width={rect.width + 16}
            height={rect.height + 16}
            rx="14"
            ry="14"
            fill="none"
            stroke="#b5e4ca"
            strokeWidth="3"
            strokeDasharray="6 4"
            className="tour-spotlight-ring"
          />
        </svg>
      )}

      {/* Centered welcome card */}
      {isCentered ? (
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
          <TourCard tour={tour} step={step} docked={false} />
        </div>
      ) : tour.collapsed ? (
        <TourPill tour={tour} step={step} />
      ) : (
        <div style={tooltipStyle} className="pointer-events-none">
          <TourCard tour={tour} step={step} docked={isDockedBottom} />
        </div>
      )}
    </div>,
    document.body
  );
}

// Minimised tour indicator — a tiny bottom-right floating pill that shows the
// step counter and a click-to-expand chevron. Nothing else is on the screen
// so the user can freely fill any input the full card was covering.
function TourPill({ tour, step }) {
  const progressPct = ((tour.stepIdx + 1) / tour.totalSteps) * 100;
  return (
    <button
      data-testid="tour-pill"
      onClick={() => tour.setCollapsed(false)}
      className="pointer-events-auto fixed bottom-24 right-4 z-[10001] flex items-center gap-2 px-3 py-2 rounded-full bg-white border-2 border-[#1a1a1a] font-bold text-xs hover:bg-[#fdecc8] transition"
      style={{ boxShadow: "3px 3px 0 0 #1a1a1a" }}
      title={`Expand walkthrough — Step ${tour.stepIdx + 1} of ${tour.totalSteps}`}
    >
      <Sparkles size={12} strokeWidth={3} className="text-[#7b6ad0]" />
      <span className="tabular-nums">
        {tour.stepIdx + 1}/{tour.totalSteps}
      </span>
      {/* Mini inline progress dot */}
      <span
        className="inline-block w-8 h-1 rounded-full bg-[#1a1a1a]/10 overflow-hidden"
        aria-hidden="true"
      >
        <span
          className="block h-full bg-[#12854a]"
          style={{ width: `${progressPct}%`, transition: "width 400ms" }}
        />
      </span>
      <Maximize2 size={12} strokeWidth={3} />
    </button>
  );
}

function TourCard({ tour, step, docked }) {
  const gateOk = tour.gateOk;
  const isLast = tour.stepIdx === tour.totalSteps - 1;
  const isFirst = tour.stepIdx === 0;
  const progressPct = ((tour.stepIdx + 1) / tour.totalSteps) * 100;
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const menuBtnRef = useRef(null);
  const embed = embedForUrl(step.videoUrl);
  const hasVideo = !!embed.src;

  // Close the menu when clicking anywhere outside it
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuBtnRef.current && menuBtnRef.current.contains(e.target)) return;
      // The menu portal has data-testid="tour-step-menu"; skip clicks inside it
      const menu = document.querySelector('[data-testid="tour-step-menu"]');
      if (menu && menu.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  return (
    <div
      data-testid={`tour-card-${step.id}`}
      className={`pointer-events-auto nb-card-lg bg-white max-w-sm w-full relative overflow-hidden ${
        docked ? "p-3" : "p-5"
      }`}
      style={{ boxShadow: "6px 6px 0 0 #1a1a1a" }}
    >
      {/* Slim progress track — sits flush at the very top of the card so
          learners can see at a glance how much of the walkthrough is left.
          Uses the mint brand accent; animates smoothly on step change. */}
      <div
        className="absolute top-0 left-0 right-0 h-1 bg-[#1a1a1a]/10"
        aria-hidden="true"
      >
        <div
          data-testid="tour-progress-bar"
          className="h-full bg-[#12854a]"
          style={{
            width: `${progressPct}%`,
            transition: "width 400ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </div>

      <div className={`flex items-center justify-between ${docked ? "mb-1" : "mb-2"}`}>
        <div className="flex items-center gap-1.5">
          <Sparkles size={16} strokeWidth={3} className="text-[#7b6ad0]" />
          <div className="label !mb-0">
            {tour.stepIdx + 1} of {tour.totalSteps}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Step menu — jump straight to any step. Handy when a user just
              wants to re-see one specific feature. */}
          <button
            ref={menuBtnRef}
            data-testid="tour-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className={`p-1.5 rounded-lg border-2 transition ${
              menuOpen
                ? "border-[#1a1a1a] bg-[#fdecc8]"
                : "border-transparent hover:border-[#1a1a1a] hover:bg-[#f4f0eb]"
            }`}
            title="Jump to any step"
            aria-label="Open step menu"
            aria-expanded={menuOpen}
          >
            <List size={16} strokeWidth={3} />
          </button>
          {/* Minimise button — only makes sense on docked (form-heavy) steps
              where the card can visually block inputs. On centered / floating
              steps the card doesn't sit on top of any user input. */}
          {docked && (
            <button
              data-testid="tour-minimize-btn"
              onClick={() => tour.setCollapsed(true)}
              className="p-1.5 rounded-lg border-2 border-transparent hover:border-[#1a1a1a] hover:bg-[#f4f0eb] transition"
              title="Minimise so you can see the form"
              aria-label="Minimise walkthrough card"
            >
              <Minus size={16} strokeWidth={3} />
            </button>
          )}
          <button
            data-testid="tour-close-btn"
            onClick={tour.close}
            className="p-1.5 rounded-lg border-2 border-transparent hover:border-[#1a1a1a] hover:bg-[#f4f0eb] transition"
            title="Close for now (you can restart from Profile)"
            aria-label="Close walkthrough for now"
          >
            <X size={16} strokeWidth={3} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <StepMenu
          tour={tour}
          onPick={(idx) => {
            setMenuOpen(false);
            tour.goToStep(idx);
          }}
        />
      )}

      <div
        className={`font-display font-black leading-tight ${
          docked ? "text-base mb-1" : "text-xl mb-1.5"
        }`}
      >
        {step.title}
      </div>
      <div
        className={`font-medium text-[#4a4a4a] leading-snug ${
          docked ? "text-xs mb-2" : "text-sm mb-4"
        }`}
      >
        {step.body}
      </div>

      {/* Welcome-only "Snap a receipt right now" shortcut. Bypasses the
          type-your-first-transaction flow for kids who'd rather just point
          their camera at a receipt and let OCR do the work. */}
      {isFirst && (
        <button
          data-testid="tour-skip-to-snap-btn"
          onClick={() => tour.skipToSnap?.()}
          className="w-full mb-3 nb-btn nb-btn-positive !py-3 !text-sm justify-center"
        >
          <Camera size={16} strokeWidth={3} /> Snap a receipt right now
        </button>
      )}

      {step.waitFor && !gateOk && (
        <div
          data-testid="tour-gate-hint"
          className={`px-2 py-1.5 rounded-lg border-2 border-dashed border-[#1a1a1a]/30 bg-[#fdecc8] font-bold ${
            docked ? "mb-2 text-[11px]" : "mb-3 text-xs"
          }`}
        >
          {step.waitFor === "account"
            ? "Fill in the form above and hit Add account — I'll unlock Next automatically."
            : "Save your first transaction — I'll unlock Next automatically."}
        </div>
      )}

      {/* Two-row action strip so the layout doesn't cram on narrow screens.
          Row 1: "Skip this step" (per-step) + primary Next.
          Row 2: "Don't show again" (permanent skip). */}
      <div className="flex items-center justify-between gap-2">
        {/* Per-step skip — hidden on the welcome step (nothing to skip yet)
            and on the last step (Finish handles it). */}
        <div className="flex items-center gap-2 min-w-0">
          {!isFirst && !isLast ? (
            <button
              data-testid="tour-skip-step-btn"
              onClick={tour.skipStep}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#4a4a4a] hover:text-[#1a1a1a] transition"
              title="Skip just this step and jump to the next one"
            >
              <SkipForward size={12} strokeWidth={3} /> Skip this step
            </button>
          ) : (
            <span />
          )}
          {hasVideo && (
            <button
              data-testid="tour-video-btn"
              onClick={() => setVideoOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#7b6ad0] hover:text-[#5a49af] transition"
              title="Watch a 5-second demo of this step"
            >
              <Play size={12} strokeWidth={3} fill="currentColor" /> Watch demo
            </button>
          )}
        </div>
        <button
          data-testid="tour-next-btn"
          onClick={tour.advance}
          disabled={step.waitFor && !gateOk}
          className={`nb-btn ${
            isLast ? "nb-btn-positive" : "nb-btn-dark"
          } !py-2 !px-3 !text-sm ${step.waitFor && !gateOk ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          {step.primary || (isLast ? "Finish" : "Next")}
          {!isLast && <ChevronRight size={14} strokeWidth={3} />}
        </button>
      </div>

      <div className="mt-2 pt-2 border-t border-[#1a1a1a]/10 flex items-center justify-end">
        <button
          data-testid="tour-skip-link"
          onClick={tour.skip}
          className="text-[11px] font-bold text-[#4a4a4a] hover:text-[#c81e1e] underline underline-offset-2 transition"
          title="Skip and don't show me the walkthrough again"
        >
          Don't show me the walkthrough again
        </button>
      </div>

      {videoOpen && (
        <VideoModal
          title={step.menuLabel || step.title}
          embed={embed}
          onClose={() => setVideoOpen(false)}
        />
      )}
    </div>
  );
}

// The jump-to-any-step menu. Rendered as a portal so it can escape the tour
// card's `overflow-hidden` (needed for the top progress bar) and float over
// whatever the spotlight is highlighting. Anchored to the viewport rather
// than the button because the tour card itself is `position: fixed` at odd
// spots — a viewport-anchored menu is far more predictable.
function StepMenu({ tour, onPick }) {
  return createPortal(
    <div
      data-testid="tour-step-menu"
      role="menu"
      className="fixed z-[10002] pointer-events-auto nb-card-lg bg-white overflow-hidden"
      style={{
        boxShadow: "6px 6px 0 0 #1a1a1a",
        top: 84,
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(320px, calc(100vw - 24px))",
        maxHeight: "70vh",
      }}
    >
      <div className="px-3 py-2 border-b-2 border-[#1a1a1a] bg-[#fdecc8] flex items-center gap-1.5">
        <List size={14} strokeWidth={3} />
        <div className="label !mb-0">Jump to any step</div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: "calc(70vh - 44px)" }}>
        {tour.steps.map((s, idx) => {
          const isCurrent = idx === tour.stepIdx;
          const isPast = idx < tour.stepIdx;
          const hasVideo = !!embedForUrl(s.videoUrl).src;
          return (
            <button
              key={s.id}
              data-testid={`tour-menu-item-${s.id}`}
              onClick={() => onPick(idx)}
              role="menuitem"
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-b border-[#1a1a1a]/10 last:border-b-0 transition font-medium text-sm ${
                isCurrent
                  ? "bg-[#b5e4ca] font-bold"
                  : "hover:bg-[#f4f0eb]"
              }`}
            >
              <span
                className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-[#1a1a1a] text-[10px] font-black tabular-nums ${
                  isCurrent
                    ? "bg-[#12854a] text-white"
                    : isPast
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-white"
                }`}
              >
                {isPast && !isCurrent ? (
                  <Check size={12} strokeWidth={4} />
                ) : (
                  idx + 1
                )}
              </span>
              <span className="flex-1 truncate">{s.menuLabel || s.title}</span>
              {hasVideo && (
                <span
                  data-testid={`tour-menu-video-badge-${s.id}`}
                  className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-black uppercase text-[#7b6ad0] bg-[#d9d4f1] px-1.5 py-0.5 rounded-full border border-[#7b6ad0]/40"
                  title="Has a demo video"
                >
                  <Play size={8} strokeWidth={4} fill="currentColor" /> Vid
                </span>
              )}
              {isCurrent && (
                <span className="text-[10px] font-black uppercase text-[#12854a]">
                  Here
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

// Lightbox player for a step's demo video. Renders an iframe (Loom / YouTube)
// or a native <video> (direct .mp4) depending on what `embedForUrl` decided.
// Portalled to document.body so it sits above the tour spotlight/dim.
function VideoModal({ title, embed, onClose }) {
  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      data-testid="tour-video-modal"
      className="fixed inset-0 z-[10003] bg-black/80 flex items-center justify-center p-4 pointer-events-auto"
      onClick={onClose}
    >
      <div
        className="relative bg-white nb-card-lg overflow-hidden w-full max-w-2xl"
        style={{ boxShadow: "6px 6px 0 0 #1a1a1a" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b-2 border-[#1a1a1a] bg-[#fdecc8]">
          <div className="flex items-center gap-1.5 min-w-0">
            <Play size={14} strokeWidth={3} fill="currentColor" className="text-[#7b6ad0]" />
            <div className="font-display font-black text-sm truncate">{title}</div>
          </div>
          <button
            data-testid="tour-video-close"
            onClick={onClose}
            className="p-1.5 rounded-lg border-2 border-transparent hover:border-[#1a1a1a] hover:bg-white transition"
            aria-label="Close video"
          >
            <X size={16} strokeWidth={3} />
          </button>
        </div>
        <div className="relative bg-black" style={{ aspectRatio: "16 / 9" }}>
          {embed.kind === "iframe" ? (
            <iframe
              src={embed.src}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : embed.kind === "video" ? (
            // Direct .mp4 file
            <video
              src={embed.src}
              controls
              autoPlay
              playsInline
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white font-bold">
              Unsupported video URL
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

